import { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch } from '../store/store';
import { fetchAlerts, selectAlertsStatus, forceRefresh } from '../store/slices/alertsSlice';

const REFRESH_INTERVAL = 30000; // 30 seconds

// Global interval manager to prevent multiple intervals
class GlobalIntervalManager {
  private static instance: GlobalIntervalManager;
  private intervalId: number | null = null;
  private activeHooks: Set<string> = new Set();
  private dispatch: AppDispatch | null = null;
  private lastSuccessfulRefresh: number = Date.now();
  private isRefreshing: boolean = false;
  private stuckTimeout: number | null = null;
  private readonly STUCK_TIMEOUT = 60000; // 60 seconds - consider a fetch "stuck" after this time

  private constructor() {}

  public static getInstance(): GlobalIntervalManager {
    if (!GlobalIntervalManager.instance) {
      GlobalIntervalManager.instance = new GlobalIntervalManager();
    }
    return GlobalIntervalManager.instance;
  }

  public register(hookId: string, dispatch: AppDispatch): void {
    this.activeHooks.add(hookId);
    this.dispatch = dispatch;
    this.startInterval();
  }

  public unregister(hookId: string): void {
    this.activeHooks.delete(hookId);
    if (this.activeHooks.size === 0) {
      this.stopInterval();
    }
  }

  private startInterval(): void {
    if (this.intervalId || !this.dispatch) return;

    this.intervalId = setInterval(() => {
      this.performScheduledRefresh();
    }, REFRESH_INTERVAL);
  }

  private performScheduledRefresh(): void {
    if (!this.dispatch) return;

    // If we're already refreshing and it's been less than the stuck timeout, skip
    if (this.isRefreshing) {
      const timeSinceRefreshStart = Date.now() - (this.lastSuccessfulRefresh + REFRESH_INTERVAL);
      if (timeSinceRefreshStart < this.STUCK_TIMEOUT) {
        console.log('Skipping refresh - already in progress');
        return;
      } else {
        console.warn('Fetch appears stuck, forcing new refresh');
        this.isRefreshing = false;
        this.clearStuckTimeout();
      }
    }

    this.startRefresh();
  }

  private startRefresh(): void {
    if (!this.dispatch) return;

    this.isRefreshing = true;

    // Set up a timeout to handle stuck requests
    this.stuckTimeout = window.setTimeout(() => {
      console.warn('Alert fetch appears to be stuck, resetting state');
      this.onRefreshComplete();
    }, this.STUCK_TIMEOUT);

    // Dispatch the fetch
    const fetchPromise = this.dispatch(fetchAlerts());

    // Handle completion
    fetchPromise
      .then(() => {
        this.onRefreshComplete();
      })
      .catch(error => {
        console.error('Alert fetch failed:', error);
        this.onRefreshComplete();
      });
  }

  private onRefreshComplete(): void {
    this.isRefreshing = false;
    this.lastSuccessfulRefresh = Date.now();
    this.clearStuckTimeout();
  }

  private clearStuckTimeout(): void {
    if (this.stuckTimeout) {
      clearTimeout(this.stuckTimeout);
      this.stuckTimeout = null;
    }
  }

  private stopInterval(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.clearStuckTimeout();
  }

  public triggerRefresh(): void {
    if (!this.dispatch) return;

    // Don't trigger if already refreshing, unless it's been too long
    if (this.isRefreshing) {
      const timeSinceRefreshStart = Date.now() - (this.lastSuccessfulRefresh + REFRESH_INTERVAL);
      if (timeSinceRefreshStart < this.STUCK_TIMEOUT) {
        return;
      }
    }

    this.startRefresh();
  }

  public getLastRefreshTime(): number {
    return this.lastSuccessfulRefresh;
  }

  public getTimeUntilRefresh(): number {
    const elapsed = Date.now() - this.lastSuccessfulRefresh;
    return Math.max(0, REFRESH_INTERVAL - elapsed);
  }

  public isCurrentlyRefreshing(): boolean {
    return this.isRefreshing;
  }
}

const globalManager = GlobalIntervalManager.getInstance();

// Export the global manager instance for use in components
export const getGlobalIntervalManager = () => globalManager;

export const useAlertsAutoRefresh = (isActive: boolean = true, hookId: string = 'default') => {
  const dispatch = useDispatch<AppDispatch>();
  const status = useSelector(selectAlertsStatus);

  // Function to trigger a refresh
  const triggerRefresh = useCallback(() => {
    globalManager.triggerRefresh();
  }, []);

  // Function to force refresh (used when navigating to alerts page)
  const forceRefreshAlerts = useCallback(() => {
    dispatch(forceRefresh());
    const fetchPromise = dispatch(fetchAlerts());
    fetchPromise.finally(() => {
      // Update the manager's state manually for force refresh
      globalManager['lastSuccessfulRefresh'] = Date.now();
      globalManager['isRefreshing'] = false;
    });
  }, [dispatch]);

  useEffect(() => {
    if (!isActive) {
      globalManager.unregister(hookId);
      return;
    }

    // Initial fetch if idle
    if (status === 'idle') {
      const fetchPromise = dispatch(fetchAlerts());
      fetchPromise.finally(() => {
        globalManager['lastSuccessfulRefresh'] = Date.now();
      });
    }

    // Register this hook with the global manager
    globalManager.register(hookId, dispatch);

    // Cleanup on unmount or when isActive changes
    return () => {
      globalManager.unregister(hookId);
    };
  }, [isActive, dispatch, status, hookId]);

  return {
    triggerRefresh,
    forceRefreshAlerts,
    isLoading: status === 'loading',
  };
};
